// Adapted from vanilla js solution here:
// https://stackoverflow.com/questions/28767221/flexbox-resizing
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
} from '@angular/core'
import { appliedUiScale } from '@helios/utils'

export interface ResizeHandleState {
  initialElem1Grow: string
  initialElem2Grow: string
}

export enum ResizeDirection {
  Vertical = 'v',
  Horizontal = 'h',
}

type ResizeProps = {
  sizeProp: 'offsetHeight' | 'offsetWidth'
  posProp: 'pageY' | 'pageX'
}

const verticalProps: ResizeProps = {
  sizeProp: 'offsetHeight',
  posProp: 'pageY',
}

const horizontalProps: ResizeProps = {
  sizeProp: 'offsetWidth',
  posProp: 'pageX',
}

/**
 * UI Element to resize two flex items within a flex container.
 *
 * @example
 * ```html
 * <div class="d-flex">
 *  <elem1 #elem1 style="flex-grow: 1;"></elem1>
 *  <hs-resize-handle [el1]="elem1" [el2]="elem2" direction="v"></hs-resize-handle>
 *  <elem2 #elem2 style="flex-grow: 1;"></elem2>
 * </div>
 * ```
 */
@Component({
  template: ` <div
    class="hs-resize-handle"
    [ngClass]="{
      'resize-h': direction === 'h',
      'resize-v': direction === 'v',
      'resize-active': resizing
    }"
  >
    <i
      class="hs-resize-handle-icon mdi"
      [ngClass]="{
        'mdi-drag-vertical': direction === 'h',
        'mdi-drag-horizontal': direction === 'v'
      }"
    ></i>
  </div>`,
  styleUrls: ['./resize-handle.component.scss'],
  selector: 'hs-resize-handle',
})
export class ResizeHandleComponent implements OnChanges, AfterViewInit {
  @Input() elem1?: HTMLElement
  @Input() elem2?: HTMLElement
  @Input() direction: ResizeDirection = ResizeDirection.Vertical
  @Input() initialResizeHandleState?: ResizeHandleState
  @Output() resizeFinished: EventEmitter<ResizeHandleState> = new EventEmitter()

  resizing = false
  height = 0

  props = verticalProps

  private elem1Size = 0
  private elem2Size = 0
  private sumSize = 0
  private sumGrow = 0
  private lastPos = 0
  private scale = 1
  private elem1Grow = 0
  private elem2Grow = 0

  constructor(readonly elRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit() {
    if (this.elem1 == null || this.elem2 == null) {
      return
    }

    if (
      this.initialResizeHandleState != null &&
      this.initialResizeHandleState.initialElem1Grow != null &&
      this.initialResizeHandleState.initialElem2Grow != null
    ) {
      this.elem1.style.flexGrow = this.initialResizeHandleState.initialElem1Grow
      this.elem2.style.flexGrow = this.initialResizeHandleState.initialElem2Grow
    } else {
      this.elem1.style.flexGrow =
        this.elem1.style.flexGrow == null ? '1' : this.elem1.style.flexGrow
      this.elem2.style.flexGrow =
        this.elem2.style.flexGrow == null ? '1' : this.elem2.style.flexGrow
    }
  }

  ngOnChanges() {
    this.props = this.direction === 'v' ? verticalProps : horizontalProps
  }

  @HostListener('mousedown', ['$event']) onMouseDown(event: MouseEvent) {
    this.startResize(event)
  }

  @HostListener('window:mouseup') onMouseUp() {
    if (!this.resizing) {
      return
    }
    this.endResize()
  }

  @HostListener('window:mousemove', ['$event']) onMouseMove(event: MouseEvent) {
    if (!this.resizing) {
      return
    }
    event.preventDefault()
    this.handleResize(event)
  }

  private startResize(event: MouseEvent) {
    this.resizing = true
    this.elRef.nativeElement.classList.add('resize-active')

    if (this.elem1 == null || this.elem2 == null) {
      return
    }

    this.elem1Size = this.elem1[this.props.sizeProp]
    this.elem2Size = this.elem2[this.props.sizeProp]
    this.sumSize = this.elem1Size + this.elem2Size
    this.elem1Grow = Number(
      this.elem1.style.flexGrow === '' ? 1 : this.elem1.style.flexGrow,
    )
    this.elem2Grow = Number(
      this.elem2.style.flexGrow === '' ? 1 : this.elem2.style.flexGrow,
    )
    this.sumGrow = this.elem1Grow + this.elem2Grow
    // Into the space offsetWidth/offsetHeight are in — see appliedUiScale.
    this.scale = appliedUiScale()
    this.lastPos = event[this.props.posProp] / this.scale
  }

  private endResize() {
    this.resizing = false
    this.elRef.nativeElement.classList.remove('resize-active')

    if (this.elem1 == null || this.elem2 == null) return
    this.resizeFinished.emit({
      initialElem1Grow: this.elem1.style.flexGrow,
      initialElem2Grow: this.elem2.style.flexGrow,
    })
  }

  private handleResize(event: MouseEvent) {
    if (this.elem1 == null || this.elem2 == null) {
      return
    }

    let pos = event[this.props.posProp] / this.scale
    const diff = pos - this.lastPos
    this.elem1Size += diff
    this.elem2Size -= diff
    if (this.elem1Size < 0) {
      this.elem2Size += this.elem1Size
      pos -= this.elem1Size
      this.elem1Size = 0
    }
    if (this.elem2Size < 0) {
      this.elem1Size += this.elem2Size
      pos += this.elem2Size
      this.elem2Size = 0
    }

    const prevGrowNew = this.sumGrow * (this.elem1Size / this.sumSize)
    const nextGrowNew = this.sumGrow * (this.elem2Size / this.sumSize)

    this.elem1.style.flexGrow = `${prevGrowNew}`
    this.elem2.style.flexGrow = `${nextGrowNew}`

    this.lastPos = pos
  }
}
